import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import FundsService from 'donor-elf-web/services/funds-service';

interface ComponentArgs {
  check?: boolean; // when true, will check the progress. defaults to true
  onProgress?: (percentUploaded: number) => void;
  onComplete: () => void;
  onError?: () => void;
}

export default class FundUploadProgressComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: any) {
    super(owner, args);
    this.getProgress();
  }

  @service declare fundsService: FundsService;
  @service declare store: Store;

  timeoutId?: number;
  last10PercentTimeoutId?: number;
  showingLast10PercentProgress = false;
  @tracked progress?: number;
  @tracked completed = false;
  @tracked hasError = false;

  get check() {
    return this.args.check ?? true;
  }

  get onFirst5PercentProgress() {
    return this.progress == null || this.progress <= 5;
  }

  get onLast10PercentProgress() {
    return (this.progress || 0) >= 90;
  }

  get progressBarStyle() {
    if (this.progress) {
      return htmlSafe(`width: ${this.progress}%`);
    } else {
      return htmlSafe('');
    }
  }

  get progressBarBackgroundClass() {
    if (this.hasError) {
      return 'bg-danger';
    }
    if (this.completed) {
      return 'bg-success';
    }
    return 'bg-brand';
  }

  @action
  async getProgress() {
    if (!this.check) {
      return;
    }

    this.timeoutId = undefined;
    let model = await this.store.findRecord(
      'fund-upload',
      this.fundsService.selectedFund!.id,
      { reload: true },
    );
    if (model.hasError) {
      this.args.onError?.();
      return;
    }

    if (!this.onLast10PercentProgress) {
      this.progress = Math.max(model.progress || 0, 5);
    }
    if (model.progress === 100) {
      this.completed = true;
      this.args.onComplete();
      return;
    }
    if (!this.onLast10PercentProgress) {
      this.args.onProgress?.(this.progress!);
    }
    if (this.onLast10PercentProgress && !this.showingLast10PercentProgress) {
      this.showingLast10PercentProgress = true;
      this.delayLast10PercentProgress();
    }
    if (this.check) {
      this.delayGetProgress();
    }
  }

  delayGetProgress() {
    this.timeoutId = window.setTimeout(this.getProgress, 3000);
  }

  @action
  async showLast10PercentProgress() {
    let newProgress = this.progress! + 1;
    if (newProgress === 99) {
      newProgress = 90;
    }
    this.progress = newProgress;
    if (this.check) {
      this.delayLast10PercentProgress();
    }
  }

  delayLast10PercentProgress() {
    this.last10PercentTimeoutId = window.setTimeout(
      this.showLast10PercentProgress,
      2500,
    );
  }

  willDestroy() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    if (this.last10PercentTimeoutId) {
      window.clearTimeout(this.last10PercentTimeoutId);
      this.last10PercentTimeoutId = undefined;
    }
  }
}
